import { saveAs } from 'file-saver';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { isAxiosError } from 'src/api/instance';
import Button, { ButtonType, ButtonVariant } from 'src/components/atoms/Button';
import { Checkbox } from 'src/components/atoms/Checkbox';
import { InputText, InputTextType } from 'src/components/atoms/InputText';
import { Loader } from 'src/components/atoms/Loader';
import Spinner from 'src/components/atoms/Spinner';
import Tag from 'src/components/atoms/Tag';
// TODO(ITL-2708): Using the fraud-analyst override codes as an interim placeholder.
// The specialty reason-code set (specialty income docs / court order / benefit
// letter / asset verification / other) is pending product sign-off — swap when
// confirmed.
import { OVERRIDE_REASON_OPTIONS } from 'src/components/folders/EntryStatusBar/constants';
import LeftPanelDetails from 'src/components/folders/ModernizedFraudAnalystReview/components/LeftPanelDetails';
import ModernSubmissionDropdown from 'src/components/folders/ModernizedFraudAnalystReview/components/ModernSubmissionDropdown';
import CustomPagination from 'src/components/folders/ModernizedFraudAnalystReview/components/PotentialMatches/CustomPagination';
import {
  Container,
  RightPanel,
  RightPanelContent,
  SendReportButton,
  SubmissionDropdownWrapper,
  TopBar,
  TopBarContent
} from 'src/components/folders/ModernizedFraudAnalystReview/styles';
import {
  CALCULATION_NOTE_MIN_LENGTH,
  CalculationNoteField,
  PreviousNoteSummary
} from 'src/components/molecules/CalculationNote';
import { Dropdown } from 'src/components/molecules/Dropdown';
import { ShowState } from 'src/components/molecules/ShowState';
import { selectors as companySelectors } from 'src/features/company/companySlice';
import useCompany from 'src/features/company/hooks/useCompany';
import entriesApi from 'src/features/entries/api';
import { useEntriesByFolderId, useEntryById } from 'src/features/entries/hooks';
import { getEntriesByFolderIdThunk, getEntryByIdThunk } from 'src/features/entries/services';
import { selectors as fdeReportSelectors } from 'src/features/fde-report/fdeReportSlice';
import useFDEReport from 'src/features/fde-report/hooks/useFDEReport';
import { getResultStatusColorTag, transformResultToReadable } from 'src/features/fdeInbox/helpers';
import { useFolderById } from 'src/features/folders/hooks';
import { getFolderByIdThunk } from 'src/features/folders/services';
import { useFrameworkUI } from 'src/features/frameworkUI/hooks';
import api, {
  PreviousNote,
  SpecialtyCalculation,
  SpecialtyCalculationDocument
} from 'src/features/incomeVerification/api';
import {
  useCompleteSpecialtyWithoutIncome,
  useManualOverride
} from 'src/features/incomeVerification/hooks';
import { ManualOverrideRequest } from 'src/features/incomeVerification/ts/interfaces';
import proofsApi from 'src/features/proofs/api';
import { useProofsFilteredByEntryId } from 'src/features/proofs/hooks';
import { getProofsByEntryIdThunk } from 'src/features/proofs/services';
import useProperty from 'src/features/property/hooks/useProperty';
import { selectors as propertySelectors } from 'src/features/property/propertySlice';
import { configVars, downloadDocumentPDF } from 'src/helpers';
import { buildAndDownloadZip, ZipFile } from 'src/helpers/buildAndDownloadZip';
import {
  buildBalFileName,
  buildProofFileName,
  sanitizeSubdir
} from 'src/helpers/specialtyDocumentFilename';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import useSnackbarProvider from 'src/hooks/useSnackbarProvider';
import { useAppDispatch } from 'src/store';
import {
  ApplicantReportPreset,
  ApplicantSubmissionDocumentSources,
  FeatureFlagKey
} from 'src/ts/enums';
import { EntryResultType, IEntry, IProof } from 'src/ts/interfaces';

import CompleteWithoutIncomeModal from './CompleteWithoutIncomeModal';
import SendReportConfirmModal from './SendReportConfirmModal';
import {
  BottomBar,
  BottomBarAction,
  BottomBarCheckbox,
  BottomBarField,
  BottomBarHelper,
  BottomBarNote,
  BottomBarRow,
  CalcEmpty,
  CalcGridCard,
  CalcGridHeader,
  CalcSummary,
  CalcTable,
  Content,
  DownloadCard,
  DownloadCardArrow,
  DownloadCardFootnote,
  DownloadCardIcon,
  DownloadCardScope,
  DownloadRow,
  FieldLabel,
  RowDownloadButton,
  SourcePill
} from './styles';

type CalcFetchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'loaded';
      calculation: SpecialtyCalculation | null;
      previousNote: PreviousNote | null;
    }
  | { status: 'error' };

type OverrideFormValues = {
  incomeAmount: string;
  assetAmount: string;
  reason: string;
  calculation_note: string;
};

// A validated override awaiting the reviewer's confirmation. Display strings are
// frozen at review time so the modal always shows exactly what was validated.
type PendingOverride = {
  payload: ManualOverrideRequest;
  monthlyIncome: string;
  assetValue: string;
  reasonLabel: string;
};

const PAGE_SIZE_DEFAULT = 5;

// Cap the number of files in a single client-side ZIP. Each is fetched and held
// in memory before zipping; an unbounded download-all across a large folder
// could exhaust the tab.
const MAX_ZIP_DOCUMENTS = 200;

const fmtMoney = (n?: number | string | null): string => {
  if (n == null) return '—';
  const num = typeof n === 'string' ? Number(n) : n;
  if (Number.isNaN(num)) return '—';
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtPeriod = (start: string, end: string): string => {
  // Force UTC so 'YYYY-MM-DD' inputs (parsed as UTC midnight) display the
  // same calendar day regardless of viewer timezone.
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  const s = new Date(start).toLocaleDateString('en-US', opts);
  const e = new Date(end).toLocaleDateString('en-US', opts);
  return `${s} – ${e}`;
};

const pillLabel = (kind?: string): string => {
  switch (kind) {
    case 'DOCUMENT_UPLOAD':
      return 'DOC_UPLOAD';
    case 'BANK_LINKING':
      return 'BANK_LINKING';
    case 'CONNECTED_PAYROLL':
      return 'CONNECTED_PAYROLL';
    case 'TAX_RETURN_LINKING':
      return 'TAX_RETURN';
    default:
      return kind ?? 'UNKNOWN';
  }
};

type ApplicantLike = { first_name?: string; last_name?: string; full_name?: string };

// Entries embed the applicant via applicant_basic.json, which often returns only
// `full_name` (no first/last). Split it so download filenames still carry a name.
const nameParts = (applicant?: ApplicantLike): { first?: string; last?: string } => {
  if (applicant?.first_name || applicant?.last_name) {
    return { first: applicant.first_name, last: applicant.last_name };
  }
  const full = (applicant?.full_name ?? '').trim();
  if (!full) return {};
  const [first, ...rest] = full.split(/\s+/);
  return { first, last: rest.join(' ') || undefined };
};

// The Bank Linking report is not a proof — it lives in applicant-experience and is
// already fronted by EPA's document/pdf passthrough (same path the Verification
// Inbox uses), keyed on the bank-linking document review's document_id.
const bankLinkingReportUrl = (entry?: IEntry | null): string | undefined => {
  const documentId = entry?.document_reviews?.find(
    (review) => review.source_type === ApplicantSubmissionDocumentSources.BANK_LINKING
  )?.document_id;
  const applicantId = entry?.applicant?.id;
  if (!documentId || !applicantId) return undefined;
  return `${configVars.epa_url}/applicants/${applicantId}/document/${documentId}/pdf`;
};

const getEntryResultIcon = (result?: EntryResultType): string => {
  switch (result) {
    case 'CLEAN':
      return '/assets/images/clean.png';
    case 'EDITED':
      return '/assets/images/edited.png';
    case 'UNDETERMINED':
      return '/assets/images/insufficient.png';
    default:
      return '/assets/images/pending.svg';
  }
};

const defaultFormValues: OverrideFormValues = {
  incomeAmount: '',
  assetAmount: '',
  reason: '',
  calculation_note: ''
};

const SpecialtyIncomeCalcPage: FC = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { entry, isError: entryError } = useEntryById(entryId ?? '');
  const folderId = entry?.folder_id ?? '';
  const { folder, isError: folderError } = useFolderById(folderId);
  const { entries: folderEntries } = useEntriesByFolderId(folderId);

  const { onGetById: onGetProperty } = useProperty();
  const propertyData = useSelector(propertySelectors.getOne.data);

  const { onGetOneById: onGetCompany } = useCompany();
  const companyData = useSelector(companySelectors.getOne.data);

  const { isOpenSidebar } = useFrameworkUI();
  const { showSnackbar, SnackTypes, VariantType } = useSnackbarProvider();
  const { onManualOverride, isOverrideLoading } = useManualOverride();
  const { onCompleteWithoutIncome, isCompleting } = useCompleteSpecialtyWithoutIncome();
  const { onDownloadPaginatedReport, onResetGetDownloadApplicantReport } = useFDEReport();
  const reportIsLoading = useSelector(fdeReportSelectors.getDownloadApplicantReport.isLoading);
  const reportError = useSelector(fdeReportSelectors.getDownloadApplicantReport.error);
  const reportFile = useSelector(fdeReportSelectors.getDownloadApplicantReport.file);
  const reportFileName = useSelector(fdeReportSelectors.getDownloadApplicantReport.fileName);
  const reportPreset = useSelector(fdeReportSelectors.getDownloadApplicantReport.preset);
  const snapptReportLoading =
    reportIsLoading && reportPreset === ApplicantReportPreset.SummaryAndDocuments;

  const [calcState, setCalcState] = useState<CalcFetchState>({ status: 'idle' });
  const isNoteEnabled = useFeatureFlag(FeatureFlagKey.ITL_3287_SPECIALTY_CALCULATION_NOTE);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  // ITL-2977: non-null once the form validates; drives the confirmation modal.
  const [pendingOverride, setPendingOverride] = useState<PendingOverride | null>(null);
  // The Modal's confirm button shows a spinner while loading but stays clickable —
  // this ref is the double-submit guard.
  const isSendingRef = useRef(false);
  // When unchecked (default), the income/asset inputs are hidden and the action
  // completes the review with the existing value (no override). Checking it reveals
  // the override fields and switches the action to the manual-override Send report.
  const [additionalIncome, setAdditionalIncome] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedEntryId, setSelectedEntryId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  const methods = useForm<OverrideFormValues>({
    mode: 'onChange',
    defaultValues: defaultFormValues
  });
  const incomeAmount = methods.watch('incomeAmount');
  const assetAmount = methods.watch('assetAmount');
  const calculationNote = methods.watch('calculation_note') ?? '';
  const isAssetOnly = incomeAmount?.trim() === '' && assetAmount?.trim() !== '';
  const isNoteRequired = isNoteEnabled && additionalIncome && !isAssetOnly;
  const canSubmit =
    !isOverrideLoading &&
    (incomeAmount?.trim() !== '' || assetAmount?.trim() !== '') &&
    (!isNoteRequired || calculationNote.trim().length >= CALCULATION_NOTE_MIN_LENGTH);

  useEffect(() => {
    if (entry?.id && !selectedEntryId) {
      setSelectedEntryId(entry.id);
    }
  }, [entry?.id, selectedEntryId]);

  useEffect(() => {
    if (folderId) {
      dispatch(getEntriesByFolderIdThunk(folderId));
    }
  }, [dispatch, folderId]);

  // Guard on ID match instead of idle status so we re-fetch when navigating
  // between submissions with different property/company. The shared Redux
  // getOne singleton holds whatever was last loaded; without this guard the
  // left panel would show stale property/company until the new fetch landed.
  useEffect(() => {
    if (folder?.property_id && propertyData?.id !== folder.property_id) {
      onGetProperty(folder.property_id);
    }
  }, [folder?.property_id, onGetProperty, propertyData?.id]);

  useEffect(() => {
    if (folder?.company_id && companyData?.id !== folder.company_id) {
      onGetCompany(folder.company_id);
    }
  }, [folder?.company_id, onGetCompany, companyData?.id]);

  // The retry button calls loadCalculation outside the effect, so an
  // effect-scoped cancelled flag can't cover those fetches — the request id
  // invalidates stale responses from any call site.
  const calcRequestIdRef = useRef(0);
  const loadCalculation = useCallback(() => {
    if (!entryId) return;
    const requestId = ++calcRequestIdRef.current;
    setCalcState({ status: 'loading' });
    api
      .getSpecialtyCalculation(entryId)
      .then((res) => {
        if (requestId !== calcRequestIdRef.current) return;
        // defaultInstance interceptor (api/instance.ts:13) unwraps to response.data
        // so `res` IS the body. Accept either { data: { calculation } } or
        // { calculation } until the BE shape is finalized.
        const calculation =
          res?.data?.calculation ??
          (res as unknown as { calculation?: SpecialtyCalculation } | null)?.calculation ??
          null;
        setCalcState({
          status: 'loaded',
          calculation,
          previousNote: res?.data?.previous_note ?? null
        });
      })
      .catch((err) => {
        if (requestId !== calcRequestIdRef.current) return;
        // A 404 means no calculation exists yet (expected until the Phase 1 BE
        // endpoint lands) — render the empty state, not the error state.
        if (isAxiosError(err) && err.response?.status === 404) {
          setCalcState({ status: 'loaded', calculation: null, previousNote: null });
          return;
        }
        setCalcState({ status: 'error' });
      });
  }, [entryId]);

  useEffect(() => {
    loadCalculation();
    return () => {
      calcRequestIdRef.current += 1;
    };
  }, [loadCalculation]);

  const submissionOptions = useMemo(() => {
    const source = folderEntries && folderEntries.length > 0 ? folderEntries : entry ? [entry] : [];
    return source.map((e) => ({
      value: e.id,
      label: new Date(e.submission_time || e.inserted_at || '').toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }),
      icon: getEntryResultIcon(e.result),
      status: e.result || 'PENDING',
      // Entries embed the applicant via applicant_basic.json, which returns
      // `full_name` (not first_name/last_name) — prefer it so the dropdown shows
      // the real name instead of "Unknown".
      applicantName:
        e.applicant?.full_name ||
        [e.applicant?.first_name, e.applicant?.last_name].filter(Boolean).join(' ') ||
        'Unknown'
    }));
  }, [folderEntries, entry]);

  const activeEntryId = selectedEntryId || entry?.id || '';
  const {
    proofs: selectedProofs,
    isLoading: proofsLoading,
    isUninitialized: proofsIdle,
    isError: proofsError
  } = useProofsFilteredByEntryId(activeEntryId);
  const proofsResolving = proofsLoading || proofsIdle;
  // proofsError is slice-global — presence of loaded proofs must win over a
  // sister submission's failed fetch.
  const proofsLoadFailed = proofsError && !proofsResolving && selectedProofs.length === 0;

  const handleRetryPage = useCallback(() => {
    if (entryId && !entry) dispatch(getEntryByIdThunk(entryId));
    if (folderId) {
      dispatch(getFolderByIdThunk(folderId));
      dispatch(getEntriesByFolderIdThunk(folderId));
    }
  }, [dispatch, entryId, entry, folderId]);

  // useProofsFilteredByEntryId only auto-fetches from IDLE, so recovering from
  // an error requires an explicit re-dispatch.
  const handleRetryProofs = useCallback(() => {
    if (activeEntryId) dispatch(getProofsByEntryIdThunk(activeEntryId));
  }, [activeEntryId, dispatch]);
  // Calc-grid rows join to proofs by id: FVS calculation document_ids ARE proof ids
  // for uploaded documents (the IV pipeline stores ReviewProofs on exactly this
  // join). Rows from non-upload sources have no matching proof — render nothing.
  const proofByDocId = useMemo(
    () => new Map(selectedProofs.map((p) => [p.id, p])),
    [selectedProofs]
  );
  // Prefer the fully-loaded route entry when ids match — folder-list entries may
  // omit `document_reviews`/`applicant.id`, which the BAL lookup needs.
  const selectedEntry =
    activeEntryId === entry?.id
      ? entry
      : folderEntries?.find((e) => e.id === activeEntryId) ?? entry ?? null;
  const balUrl = bankLinkingReportUrl(selectedEntry);

  const documents: SpecialtyCalculationDocument[] =
    calcState.status === 'loaded' && calcState.calculation ? calcState.calculation.documents : [];

  const paginatedDocs = useMemo(
    () => documents.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    [documents, currentPage, pageSize]
  );

  const monthlyIncome =
    calcState.status === 'loaded' && calcState.calculation?.monthly_income !== undefined
      ? fmtMoney(calcState.calculation.monthly_income)
      : '—';

  // Document counts shown on the cards. submissionDocCount is the selected
  // submission's proof count (loaded above). folderDocCount across submissions is
  // not known until the user triggers the download (we fetch per-submission then),
  // so the all-submissions card shows the submission count and the post-download
  // snackbar reports the true document total.
  const submissionDocCount = selectedProofs.length;
  const submissionCount = submissionOptions.length || 1;

  const proofsToZipFiles = (
    proofs: IProof[],
    applicant?: ApplicantLike,
    subdir?: string
  ): ZipFile[] => {
    const { first, last } = nameParts(applicant);
    return proofs
      .filter((p) => !!p.file)
      .map((p) => {
        const name = buildProofFileName(first, last, p.type, p.short_id);
        return { name: subdir ? `${subdir}/${name}` : name, url: p.file as string };
      });
  };

  const downloadResultSnackbar = (added: number, skipped: number, suffix = '') => {
    if (added === 0) {
      showSnackbar(VariantType.error, 'No documents could be downloaded.', SnackTypes.none);
      return;
    }
    showSnackbar(
      skipped > 0 ? VariantType.warning : VariantType.success,
      `Downloaded ${added} document${added === 1 ? '' : 's'}${suffix}${
        skipped ? ` (${skipped} unavailable)` : ''
      }.`,
      SnackTypes.none
    );
  };

  const handleDownloadSubmission = async () => {
    if (isDownloading || !selectedEntry) return;
    const { first, last } = nameParts(selectedEntry.applicant);
    const files = proofsToZipFiles(selectedProofs, selectedEntry.applicant);
    if (balUrl) {
      files.push({ name: buildBalFileName(first, last), url: balUrl });
    }
    if (files.length === 0) {
      showSnackbar(VariantType.error, 'No documents available to download.', SnackTypes.none);
      return;
    }
    if (files.length > MAX_ZIP_DOCUMENTS) {
      showSnackbar(
        VariantType.error,
        `Too many documents to download at once (max ${MAX_ZIP_DOCUMENTS}).`,
        SnackTypes.none
      );
      return;
    }
    setIsDownloading(true);
    try {
      const zipName = `${sanitizeSubdir(first)}_${sanitizeSubdir(last)}_documents.zip`;
      const { added, skipped } = await buildAndDownloadZip(zipName, files);
      downloadResultSnackbar(added, skipped);
    } catch {
      showSnackbar(VariantType.error, 'Download failed. Try again.', SnackTypes.none);
    } finally {
      setIsDownloading(false);
    }
  };

  // Entries embed the applicant via applicant_basic.json (often full_name only);
  // the report hook requires an explicit full_name for the PDF filename.
  const reportApplicantId = selectedEntry?.applicant?.id;
  const reportApplicantName =
    selectedEntry?.applicant?.full_name ||
    [selectedEntry?.applicant?.first_name, selectedEntry?.applicant?.last_name]
      .filter(Boolean)
      .join(' ');

  // Save-then-reset pattern copied from ReportPage/HeaderPrints: the fetched
  // report blob lands in the fde-report slice; persist it and clear the slice.
  useEffect(() => {
    if (!reportIsLoading && reportError === null && reportFile) {
      downloadDocumentPDF(reportFile, reportFileName);
      onResetGetDownloadApplicantReport();
    }
  }, [reportIsLoading, reportError, reportFile, reportFileName, onResetGetDownloadApplicantReport]);

  useEffect(() => {
    if (reportError !== null) {
      showSnackbar(VariantType.error, reportError, SnackTypes.none);
      onResetGetDownloadApplicantReport();
    }
  }, [reportError, onResetGetDownloadApplicantReport, showSnackbar, VariantType, SnackTypes]);

  const handleDownloadSnapptReport = () => {
    if (!selectedEntry?.id || !reportApplicantId || !reportApplicantName) return;
    onDownloadPaginatedReport(
      { id: selectedEntry.id },
      { id: reportApplicantId, full_name: reportApplicantName }
    );
  };

  const handleDownloadCalculatedDoc = async (docId: string) => {
    if (isDownloading || !selectedEntry) return;
    const proof = proofByDocId.get(docId);
    if (!proof?.file) {
      showSnackbar(VariantType.error, 'Document not available to download.', SnackTypes.none);
      return;
    }
    setIsDownloading(true);
    try {
      // Same fetch mechanism the ZIP pipeline uses (buildAndDownloadZip.fetchBlob) —
      // proof URLs are CORS-cleared for it. Single file, so save directly, no ZIP.
      const response = await fetch(proof.file, { credentials: 'include' });
      if (!response.ok) throw new Error(`fetch ${response.status}`);
      const blob = await response.blob();
      const { first, last } = nameParts(selectedEntry.applicant);
      saveAs(blob, buildProofFileName(first, last, proof.type, proof.short_id));
    } catch {
      showSnackbar(VariantType.error, 'Download failed. Try again.', SnackTypes.none);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    if (isDownloading) return;
    const source = folderEntries && folderEntries.length > 0 ? folderEntries : entry ? [entry] : [];
    if (source.length === 0) return;

    setIsDownloading(true);
    try {
      // The detail endpoint (unlike the folder-list one) carries
      // document_reviews/applicant.id, so every submission's Bank Linking report
      // resolves. Direct API calls, not thunks — sister-submission dispatches
      // would flip the global slice statuses and re-trigger the loading/error UI.
      const perSubmission = await Promise.all(
        source.map(async (e) => {
          try {
            const [proofsRes, entryRes] = await Promise.all([
              proofsApi.getProofsByEntryId(e.id),
              entriesApi.getEntryById(e.id)
            ]);
            const entryProofs = (proofsRes?.data || []) as unknown as IProof[];
            const detail = (entryRes?.data ?? null) as unknown as IEntry | null;
            const applicant = detail?.applicant ?? e.applicant;
            const option = submissionOptions.find((o) => o.value === e.id);
            const subdir = sanitizeSubdir(
              `${option?.applicantName ?? 'submission'}_${option?.label ?? e.id.slice(0, 8)}`
            );
            const submissionFiles = proofsToZipFiles(entryProofs, applicant, subdir);
            const entryBalUrl = bankLinkingReportUrl(detail);
            if (entryBalUrl) {
              const { first, last } = nameParts(applicant);
              submissionFiles.push({
                name: `${subdir}/${buildBalFileName(first, last)}`,
                url: entryBalUrl
              });
            }
            return submissionFiles;
          } catch {
            // Skip a submission whose fetch fails; the rest still download.
            return [];
          }
        })
      );
      const files: ZipFile[] = perSubmission.flat();

      if (files.length === 0) {
        showSnackbar(VariantType.error, 'No documents available to download.', SnackTypes.none);
        return;
      }
      if (files.length > MAX_ZIP_DOCUMENTS) {
        showSnackbar(
          VariantType.error,
          `Too many documents to download at once (max ${MAX_ZIP_DOCUMENTS}).`,
          SnackTypes.none
        );
        return;
      }
      const { first, last } = nameParts(entry?.applicant);
      const zipName = `${sanitizeSubdir(first)}_${sanitizeSubdir(last)}_all_documents.zip`;
      const { added, skipped } = await buildAndDownloadZip(zipName, files);
      downloadResultSnackbar(
        added,
        skipped,
        ` across ${source.length} submission${source.length === 1 ? '' : 's'}`
      );
    } catch {
      showSnackbar(VariantType.error, 'Download failed. Try again.', SnackTypes.none);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadBankLinkingReport = () => {
    if (!balUrl) return;
    // Mirror the Verification Inbox: navigate to EPA's document/pdf passthrough,
    // which streams the report as an attachment.
    window.location.href = balUrl;
  };

  // ITL-2977: validation + payload build on the button click; the submit itself
  // waits behind the confirmation modal. Every validation failure returns before
  // setPendingOverride, so invalid input never opens the modal.
  const handleReviewOverride = async (values: OverrideFormValues) => {
    methods.clearErrors();

    const hasIncome = values.incomeAmount.trim() !== '';
    const hasAsset = values.assetAmount.trim() !== '';

    if (!hasIncome && !hasAsset) {
      showSnackbar(VariantType.error, 'Enter income, asset, or both.', SnackTypes.none);
      return;
    }
    if (!values.reason) {
      methods.setError('reason', { type: 'manual', message: 'Select a reason' });
      return;
    }

    const payload: ManualOverrideRequest = {};
    if (hasIncome) {
      const parsed = Number(values.incomeAmount);
      if (Number.isNaN(parsed) || parsed <= 0) {
        methods.setError('incomeAmount', {
          type: 'manual',
          message: 'Enter a valid positive amount'
        });
        return;
      }
      payload.income_override = {
        monthly_gross_income: parsed,
        override_reason_code: Number(values.reason)
      };
    }
    if (hasAsset) {
      const parsed = Number(values.assetAmount);
      if (Number.isNaN(parsed) || parsed <= 0) {
        methods.setError('assetAmount', {
          type: 'manual',
          message: 'Enter a valid positive amount'
        });
        return;
      }
      payload.asset_override = {
        total_verified_assets: parsed,
        override_reason_code: Number(values.reason)
      };
    }

    if (isNoteRequired) {
      payload.note = values.calculation_note.trim();
    }

    const reasonLabel =
      OVERRIDE_REASON_OPTIONS.find((o) => o.value === values.reason)?.label ?? values.reason;
    setPendingOverride({
      payload,
      monthlyIncome: hasIncome ? fmtMoney(payload.income_override?.monthly_gross_income) : '—',
      assetValue: hasAsset ? fmtMoney(payload.asset_override?.total_verified_assets) : '—',
      reasonLabel
    });
  };

  const handleConfirmSend = async () => {
    if (!pendingOverride || isSendingRef.current) return;
    isSendingRef.current = true;
    try {
      const ok = await onManualOverride(entryId, pendingOverride.payload);
      // Close on any outcome (like CompleteWithoutIncomeModal); on failure the
      // hook's snackbar reports it and the intact form makes retry one click.
      setPendingOverride(null);
      if (ok) {
        methods.reset(defaultFormValues);
        navigate('/my-work');
      }
    } finally {
      isSendingRef.current = false;
    }
  };

  // Once confirm fires the request can't be recalled — ignore cancel (button,
  // background click) until it settles, so the settled branch can't navigate
  // out from under a user who thinks they canceled.
  const handleCancelSend = () => {
    if (isSendingRef.current) return;
    setPendingOverride(null);
  };

  // ITL-2933: complete the specialty review and release the report with no income
  // value (no override recorded). Always available, independent of the income/asset
  // form — gated behind a confirmation because the release is irreversible.
  // ITL-2953: the confirmation requires a no-income reason, sent as reason_code.
  const handleCompleteWithoutIncome = async (reasonCode: number) => {
    const ok = await onCompleteWithoutIncome(entryId, reasonCode);
    if (ok) {
      methods.reset(defaultFormValues);
      navigate('/my-work');
    }
  };

  // Unchecking unmounts the override inputs, but RHF keeps their values
  // (shouldUnregister: false) — clear them so re-checking doesn't restore stale input.
  const handleAdditionalIncomeToggle = (checked: boolean) => {
    setAdditionalIncome(checked);
    if (!checked) {
      methods.reset(defaultFormValues);
    }
  };

  // Slice statuses are global: presence keeps the page rendered through later
  // status churn, and each error flag pairs with its own missing record so a
  // sibling-list failure can't be misattributed while the folder loads.
  if (!entry || !folder) {
    if ((!entry && entryError) || (!folder && folderError)) {
      return (
        <Container isMainSidebarOpen={isOpenSidebar} data-testid="specialty-income-calc-error">
          <ShowState
            variant="error"
            type="application"
            message="We couldn't load this submission."
            buttonLabel="Retry"
            onClick={handleRetryPage}
          />
        </Container>
      );
    }
    return <Loader />;
  }

  return (
    <Container isMainSidebarOpen={isOpenSidebar} data-testid="specialty-income-calc-page">
      <LeftPanelDetails
        entry={entry}
        folder={folder ?? null}
        currentManualIndex={0}
        manualProofCount={0}
        propertyData={propertyData || undefined}
        companyData={companyData || undefined}
        hasMatches={false}
        matchesCount={0}
        showPotentialMatches={() => undefined}
        onReviewReport={() => undefined}
        isSubmissionReviewMode
        hideReviewReport
      />
      <RightPanel>
        <RightPanelContent>
          <TopBar>
            <TopBarContent>
              <SubmissionDropdownWrapper>
                <ModernSubmissionDropdown
                  id="submission-dropdown"
                  value={selectedEntryId || entry.id}
                  onChange={(value: string) => setSelectedEntryId(value)}
                  options={submissionOptions}
                  assignedEntryId={entry.id}
                  placeholder="Submission"
                  width="180px"
                />
              </SubmissionDropdownWrapper>
            </TopBarContent>
          </TopBar>

          <Content>
            <CalcGridCard>
              <CalcGridHeader>
                <div>
                  <h3>What we already calculated</h3>
                  <p>Reference. Auto-calc result for this submission.</p>
                </div>
                <CalcSummary>
                  <div className="label">Auto-calc monthly income</div>
                  <div className="value">{monthlyIncome}</div>
                </CalcSummary>
              </CalcGridHeader>

              {isNoteEnabled && calcState.status === 'loaded' && (
                <PreviousNoteSummary previousNote={calcState.previousNote} />
              )}

              {(calcState.status === 'idle' || calcState.status === 'loading') && (
                <CalcEmpty data-testid="calc-grid-loading">
                  <Spinner size="normal" color="primary" centered />
                </CalcEmpty>
              )}

              {calcState.status === 'error' && (
                <CalcEmpty data-testid="calc-grid-error">
                  Unable to load calculation.
                  <Button
                    name="calcGridErrorRetry"
                    label="Retry loading calculation"
                    variant={ButtonVariant.outline}
                    type={ButtonType.button}
                    onClick={loadCalculation}
                    data-testid="calc-grid-error-retry"
                  >
                    Retry
                  </Button>
                </CalcEmpty>
              )}

              {calcState.status === 'loaded' && documents.length === 0 && (
                <CalcEmpty data-testid="calc-grid-empty">
                  Nothing calculated — starting from scratch.
                </CalcEmpty>
              )}

              {documents.length > 0 && (
                <>
                  <CalcTable>
                    <thead>
                      <tr>
                        <th>Source type</th>
                        <th>Income source</th>
                        <th>Period</th>
                        <th className="num">Gross income</th>
                        <th>Ruling</th>
                        <th aria-label="Download" />
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedDocs.map((doc, i) => {
                        const rowIndex = i + currentPage * pageSize;
                        const calcSourceType =
                          calcState.status === 'loaded' && calcState.calculation
                            ? calcState.calculation.source_type
                            : undefined;
                        const kind = doc.source_type ?? calcSourceType ?? 'UNKNOWN';
                        const proof = proofByDocId.get(doc.document_id);
                        return (
                          <tr key={doc.document_id} data-testid={`calc-grid-row-${rowIndex}`}>
                            <td>
                              <SourcePill kind={kind}>{pillLabel(kind)}</SourcePill>
                            </td>
                            <td>{doc.income_source}</td>
                            <td>{fmtPeriod(doc.document_start_date, doc.document_end_date)}</td>
                            <td className="num">{fmtMoney(doc.gross_income)}</td>
                            <td>
                              {proof?.result && (
                                <Tag
                                  label={transformResultToReadable(proof.result)}
                                  color={getResultStatusColorTag(proof.result)}
                                  showBackground
                                  data-testid={`calc-grid-ruling-${rowIndex}`}
                                />
                              )}
                            </td>
                            <td>
                              {proof && (
                                <RowDownloadButton
                                  type="button"
                                  data-testid={`download-calc-doc-${rowIndex}`}
                                  onClick={() => handleDownloadCalculatedDoc(doc.document_id)}
                                  disabled={isDownloading}
                                >
                                  <img
                                    src="/assets/images/download-icon.svg"
                                    alt="Download document"
                                  />
                                </RowDownloadButton>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </CalcTable>
                  <CustomPagination
                    currentPage={currentPage}
                    pageSize={pageSize}
                    totalItems={documents.length}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setCurrentPage(0);
                    }}
                  />
                </>
              )}
            </CalcGridCard>

            <DownloadRow>
              {proofsLoadFailed ? (
                <div data-testid="download-proofs-error">
                  <ShowState
                    variant="error"
                    type="application"
                    message="Couldn't load documents for this submission."
                    buttonLabel="Retry"
                    onClick={handleRetryProofs}
                  />
                </div>
              ) : (
                <>
                  <DownloadCard
                    type="button"
                    data-testid="download-this-submission"
                    onClick={handleDownloadSubmission}
                    disabled={isDownloading || snapptReportLoading || proofsResolving}
                  >
                    <DownloadCardIcon>
                      <img src="/assets/images/download-icon.svg" alt="" />
                    </DownloadCardIcon>
                    <h3>Download for this submission</h3>
                    <DownloadCardScope>
                      <strong>{submissionDocCount} documents</strong> from this submission
                    </DownloadCardScope>
                    <DownloadCardFootnote>
                      <span>ZIP</span>
                      <DownloadCardArrow>→</DownloadCardArrow>
                    </DownloadCardFootnote>
                  </DownloadCard>
                  {submissionCount > 1 && (
                    <DownloadCard
                      type="button"
                      data-testid="download-all-submissions"
                      onClick={handleDownloadAll}
                      disabled={isDownloading || snapptReportLoading || proofsResolving}
                    >
                      <DownloadCardIcon>
                        <img src="/assets/images/download-paginated.svg" alt="" />
                      </DownloadCardIcon>
                      <h3>Download for all submissions</h3>
                      <DownloadCardScope>
                        All documents across <strong>{submissionCount} submissions</strong> in this
                        folder
                      </DownloadCardScope>
                      <DownloadCardFootnote>
                        <span>ZIP · grouped by submission</span>
                        <DownloadCardArrow>→</DownloadCardArrow>
                      </DownloadCardFootnote>
                    </DownloadCard>
                  )}
                </>
              )}
              {balUrl && (
                <DownloadCard
                  type="button"
                  data-testid="download-bank-linking-report"
                  onClick={handleDownloadBankLinkingReport}
                  disabled={isDownloading || snapptReportLoading}
                >
                  <DownloadCardIcon>
                    <img src="/assets/images/download-icon.svg" alt="" />
                  </DownloadCardIcon>
                  <h3>Download Bank Linking report</h3>
                  <DownloadCardScope>
                    Finicity bank-linking report for this submission
                  </DownloadCardScope>
                  <DownloadCardFootnote>
                    <span>PDF</span>
                    <DownloadCardArrow>→</DownloadCardArrow>
                  </DownloadCardFootnote>
                </DownloadCard>
              )}
              {reportApplicantId && reportApplicantName && (
                <DownloadCard
                  type="button"
                  data-testid="download-snappt-report"
                  onClick={handleDownloadSnapptReport}
                  disabled={isDownloading || snapptReportLoading}
                >
                  <DownloadCardIcon>
                    <img src="/assets/images/download-paginated.svg" alt="" />
                  </DownloadCardIcon>
                  <h3>Download Snappt report</h3>
                  <DownloadCardScope>Current fraud report for this submission</DownloadCardScope>
                  <DownloadCardFootnote>
                    <span>PDF</span>
                    <DownloadCardArrow>→</DownloadCardArrow>
                  </DownloadCardFootnote>
                </DownloadCard>
              )}
            </DownloadRow>
          </Content>

          <FormProvider {...methods}>
            <BottomBar>
              <BottomBarCheckbox>
                <Checkbox
                  name="additional-income"
                  value="additional-income"
                  label="Additional income or assets calculated"
                  showLabel
                  isChecked={additionalIncome}
                  onClick={(cb) => handleAdditionalIncomeToggle(!!cb.isChecked)}
                  data-testid="checkbox-additional-income"
                />
              </BottomBarCheckbox>

              <BottomBarRow>
                {additionalIncome ? (
                  <>
                    <BottomBarField>
                      <FieldLabel>Total Monthly Income</FieldLabel>
                      <InputText
                        name="incomeAmount"
                        placeholder="$0.00"
                        type={InputTextType.number}
                        showOptionalLabel={false}
                        showLabel={false}
                        data-testid="override-income-amount"
                      />
                    </BottomBarField>
                    <BottomBarField>
                      <FieldLabel>Total Asset Value</FieldLabel>
                      <InputText
                        name="assetAmount"
                        placeholder="$0.00"
                        type={InputTextType.number}
                        showOptionalLabel={false}
                        showLabel={false}
                        data-testid="override-asset-amount"
                      />
                    </BottomBarField>
                    <BottomBarField>
                      <FieldLabel>Override reason</FieldLabel>
                      <Dropdown
                        name="reason"
                        placeholder="Select…"
                        data={OVERRIDE_REASON_OPTIONS}
                        listPosition="top"
                        data-testid="override-reason-select"
                      />
                    </BottomBarField>
                  </>
                ) : (
                  <BottomBarHelper>
                    No additional income — the report sends with the existing calculated value.
                  </BottomBarHelper>
                )}

                <BottomBarAction>
                  {additionalIncome ? (
                    <SendReportButton
                      type="button"
                      data-testid="button-send-report"
                      onClick={methods.handleSubmit(handleReviewOverride)}
                      disabled={!canSubmit || isOverrideLoading || isCompleting}
                    >
                      Send report
                      <img src="/assets/images/send-report.svg" alt="" />
                    </SendReportButton>
                  ) : (
                    <SendReportButton
                      type="button"
                      data-testid="button-complete-without-income"
                      onClick={() => setShowCompleteModal(true)}
                      disabled={isOverrideLoading || isCompleting}
                    >
                      Complete report
                      <img src="/assets/images/send-report.svg" alt="" />
                    </SendReportButton>
                  )}
                </BottomBarAction>
              </BottomBarRow>

              {isNoteRequired && (
                <BottomBarNote>
                  <CalculationNoteField />
                </BottomBarNote>
              )}
            </BottomBar>
          </FormProvider>
        </RightPanelContent>
      </RightPanel>
      <CompleteWithoutIncomeModal
        show={showCompleteModal}
        setShow={setShowCompleteModal}
        onConfirm={handleCompleteWithoutIncome}
      />
      <SendReportConfirmModal
        show={pendingOverride !== null}
        monthlyIncome={pendingOverride?.monthlyIncome ?? '—'}
        assetValue={pendingOverride?.assetValue ?? '—'}
        reasonLabel={pendingOverride?.reasonLabel ?? ''}
        note={pendingOverride?.payload.note}
        isLoading={isOverrideLoading}
        onConfirm={handleConfirmSend}
        onCancel={handleCancelSend}
      />
    </Container>
  );
};

export default SpecialtyIncomeCalcPage;
